import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  pairwise,
  Subject,
} from 'rxjs';
import { FamilyMemberType } from 'src/app/_enums/family-member-type.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { User } from 'src/app/_types/auth.types';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { Lead } from 'src/app/_types/lead.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';

export type TaskDetailsState = {
  isLoading: boolean;
  insuranceFlowData: InsuranceFlowDetailsResponse | null;
  userProfile: User | null;
  lead: Lead | null;
  proposals: ProposalDetails[];
  policy: ProposalDetails | null;
  familyMemberName: string | null;
  relation: FamilyMemberType | null;
  insuranceType: InsuranceType | null;
};

const INITIAL_STATE: TaskDetailsState = {
  isLoading: false,
  insuranceFlowData: null,
  userProfile: null,
  lead: null,
  proposals: [],
  policy: null,
  familyMemberName: null,
  relation: null,
  insuranceType: null,
};

/**
 * Mediator that centralizes task-details state and cross-component actions.
 */
@Injectable()
export class TaskDetailsService {
  private readonly stateSubject = new BehaviorSubject<TaskDetailsState>(
    INITIAL_STATE
  );

  private lastRequestedFlowId: string | null = null;

  /**
   * Something the sheet does not own changed for this affaire — a cancellation
   * opened on its policy, say. The flow's own status does not move, so a host
   * watching the status would never hear about it.
   */
  private readonly changedSubject = new Subject<void>();

  readonly state$ = this.stateSubject.asObservable();

  /** Emits when the status of the currently loaded flow changes (not when a different flow is loaded). */
  readonly statusChanged$: Observable<void> = this.state$.pipe(
    map((state) => ({
      id: state.insuranceFlowData?.id,
      status: state.insuranceFlowData?.status,
    })),
    pairwise(),
    filter(
      ([previousTask, currentTask]) =>
        !!previousTask.id &&
        previousTask.id === currentTask.id &&
        !!previousTask.status &&
        !!currentTask.status &&
        previousTask.status !== currentTask.status
    ),
    map(() => undefined)
  );

  readonly changed$ = this.changedSubject.asObservable();

  constructor(
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly userAuthService: UserAuthService
  ) {}

  get state(): TaskDetailsState {
    return this.stateSubject.value;
  }

  clearTaskDetails(): void {
    this.lastRequestedFlowId = null;
    this.stateSubject.next(INITIAL_STATE);
  }

  /** Tells whoever hosts the sheet that this affaire reads differently now. */
  notifyChanged(): void {
    this.changedSubject.next();
  }

  refreshTaskDetails(): void {
    if (!this.lastRequestedFlowId) {
      return;
    }

    this.loadTaskDetails(this.lastRequestedFlowId);
  }

  loadTaskDetails(flowId: string): void {
    this.lastRequestedFlowId = flowId;
    this.updateState({ isLoading: true });

    this.insuranceFlowService.getInsuranceFlowDetails(flowId).subscribe({
      next: (flowDetailsResponse) => {
        if (this.lastRequestedFlowId !== flowId) {
          return;
        }

        const insuranceFlowData = flowDetailsResponse?.data ?? null;
        if (!insuranceFlowData) {
          this.clearTaskDetails();
          return;
        }

        this.updateState({
          insuranceFlowData,
          lead: insuranceFlowData.lead ?? null,
          proposals: insuranceFlowData.proposals ?? [],
          policy: insuranceFlowData.policy ?? null,
          familyMemberName: insuranceFlowData.memberName ?? null,
          relation: insuranceFlowData.lead?.relation ?? null,
          insuranceType:
            insuranceFlowData.insuranceType ??
            insuranceFlowData.lead?.insuranceType ??
            null,
        });

        const memberId = insuranceFlowData.userId;
        if (!memberId) {
          this.updateState({ userProfile: null, isLoading: false });
          return;
        }

        this.userAuthService.getUserDetails(memberId).subscribe({
          next: (userResponse) => {
            if (this.lastRequestedFlowId !== flowId) {
              return;
            }

            this.updateState({
              userProfile: userResponse?.data ?? null,
              isLoading: false,
            });
          },
          error: () => {
            if (this.lastRequestedFlowId !== flowId) {
              return;
            }

            this.updateState({
              userProfile: null,
              isLoading: false,
            });
          },
        });
      },
      error: () => {
        if (this.lastRequestedFlowId !== flowId) {
          return;
        }

        this.clearTaskDetails();
      },
    });
  }

  private updateState(partialState: Partial<TaskDetailsState>): void {
    this.stateSubject.next({
      ...this.state,
      ...partialState,
    });
  }
}
